import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';
import storage from 'redux-persist/lib/storage';

import mainApi from './apis/mainApi';
import launchpadSplashMiddleware from './middleware/launchpadSplash';
import translatesMiddleware from './middleware/translates';
import auth from './slices/auth/slice';
import companies from './slices/companies/slice';
import companyAdminUsers from './slices/companyAdminUsers/slice';
import dataSets from './slices/dataSets/slice';
import general from './slices/general/slice';
import iroStandardsAdmin from './slices/iroStandardsAdmin/slice';
import layoutsMA from './slices/layoutsMA/slice';
import localStorage from './slices/localStorage/slice';
import materialAssessment from './slices/materialAssessment/slice';
import notifications from './slices/notifications/slice';
import standards from './slices/standards/slice';
import survey from './slices/survey/slice';
import tasksManagement from './slices/tasksManagement/slice';
import theme from './slices/theme/slice';
import translates from './slices/translates/slice';
import valueChainMap from './slices/valueChainMap/slice';

const persistConfig = {
  key: 'root',
  version: 1,
  storage,
  whitelist: ['auth', 'theme', 'localStorage'],
};

const valueChainMapPersistConfig = {
  key: 'valueChainMap',
  storage,
  whitelist: ['currentCompanyMapTab'],
};

const persistedReducer = persistReducer(
  persistConfig,
  combineReducers({
    [mainApi.reducerPath]: mainApi.reducer,
    auth,
    translates,
    theme,
    companies,
    general,
    companyAdminUsers,
    dataSets,
    valueChainMap: persistReducer(valueChainMapPersistConfig, valueChainMap),
    localStorage,
    tasksManagement,
    materialAssessment,
    notifications,
    standards,
    survey,
    layoutsMA,
    iroStandardsAdmin,
  }),
);

type TRootReducerState = ReturnType<typeof persistedReducer>;

export function makeStore(preloadedState: Partial<TRootReducerState> = {}) {
  return configureStore({
    reducer: persistedReducer,
    // Redux 5 types `preloadedState` as the full state; callers legitimately pass a partial
    preloadedState: (Object.keys(preloadedState).length
      ? preloadedState
      : undefined) as TRootReducerState | undefined,
    middleware: (getDefaultMiddleware) => getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat([
        mainApi.middleware,
        translatesMiddleware,
        launchpadSplashMiddleware,
      ]),
  });
}

export const store = makeStore();

setupListeners(store.dispatch);

export const persistor = persistStore(store);
